import { E2E_TEST_MODE_COOKIE_NAME } from '@terra/shared-utils';
import { NextRequest, NextResponse } from 'next/server';
import { isPublicUrl } from './utils';
import {
  addSessionToHeadersIfExists,
  getGlobalSdk,
  getSessionJwt,
} from './utils';
import type { NextFetchEvent } from 'next/server';
import type { MiddlewareFactory, NextMiddleware } from '../types';
import {
  isTerraProxyAuthPublicPathname,
  isTerraProxyE2eHarnessPublicForCookie,
  TERRA_PROXY_AUTH_PUBLIC_ROUTES,
} from './public-routes';

export const runtime = 'nodejs';

const baseUrl =
  process.env.NODE_ENV === 'development'
    ? 'https://api.descope.com'
    : undefined;
const redirectUrl = '/sign-in';

const resolveProjectId = (override?: string): string | undefined =>
  override ||
  process.env._NEXT_PUBLIC_DESCOPE_PROJECT_ID ||
  process.env.NEXT_PUBLIC_DESCOPE_PROJECT_ID ||
  undefined;

export const createAuthenticationMiddleware = ({
  projectId: projectIdOverride,
}: {
  projectId?: string;
} = {}): MiddlewareFactory => {
  const projectId = resolveProjectId(projectIdOverride);

  return (next: NextMiddleware) => {
    return async (req: NextRequest, _next: NextFetchEvent) => {
      const { nextUrl } = req;

      if (isAuthPublicUrl(req)) {
        return next(req, _next);
      }

      const jwt = getSessionJwt(req);

      if (!jwt) {
        const encodedCallbackUrl = encodeURIComponent(nextUrl.pathname);
        const authRedirectUrl = `${redirectUrl}?callbackUrl=${encodedCallbackUrl}`;
        return NextResponse.redirect(new URL(authRedirectUrl, req.url));
      }

      try {
        if (!projectId) {
          throw new Error('Descope project ID is not configured');
        }

        const validSdk = getGlobalSdk({
          projectId,
          baseUrl,
        });
        const session = await validSdk.validateJwt(jwt);

        const headersWithSession = addSessionToHeadersIfExists(
          req.headers,
          session,
        );
        const requestWithSession = new NextRequest(req, {
          headers: headersWithSession,
        });

        return next(requestWithSession, _next);
      } catch {
        const encodedCallbackUrl = encodeURIComponent(nextUrl.pathname);
        const authRedirectUrl = `${redirectUrl}?callbackUrl=${encodedCallbackUrl}`;
        return NextResponse.redirect(new URL(authRedirectUrl, req.url));
      }
    };
  };
};

export function isAuthPublicPathname(
  pathname: string | null | undefined,
): boolean {
  if (!pathname) {
    return false;
  }
  return isTerraProxyAuthPublicPathname(pathname);
}

function isAuthPublicUrl(request: NextRequest): boolean {
  if (isPublicUrl(request, [...TERRA_PROXY_AUTH_PUBLIC_ROUTES])) {
    return true;
  }

  return isTerraProxyE2eHarnessPublicForCookie({
    pathname: request.nextUrl.pathname,
    cookieValue: request.cookies.get(E2E_TEST_MODE_COOKIE_NAME)?.value,
  });
}
